import { useState } from 'react';
import { Modal, Button, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { loadStripe } from '@stripe/stripe-js';
import PropTypes from 'prop-types';
import { updateOrderExtension, stripeExtensionCheckout } from '../../../../../data/API';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PAYMENT_KEY);

const RentalExtensionSummaryModal = ({ 
  open, 
  onClose, 
  summaryData, 
  isProcessing,
  selectedExtendOrder,
  selectedOrderDetails,
  customer,
  onSuccess
}) => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handlePayAndRequest = async () => {
    try {
      setIsProcessingPayment(true);
      
      const orderId = selectedExtendOrder.order_id;
      const updateData = {
        extension_weeks: summaryData?.extensionWeeks,
        extension_days: summaryData?.extensionDays,
        extension_type: summaryData?.extensionType,
        pickup_date: summaryData?.requestData?.pickup_date,
        intended_pickup_date: summaryData?.requestData?.intended_pickup_date, // Pass intended date
        total_extension_price: summaryData?.totalCost,
        box_package_price: summaryData?.requestData?.box_package_price,
        moving_supplies_price: summaryData?.requestData?.moving_supplies_price
      };
      
      console.log('Updating order with extension data:', updateData);
      let updateRes = await updateOrderExtension(orderId, updateData);
      console.log('Order update response:', updateRes);
      
      if (updateRes?.data?.status === 'success') {
        const checkoutData = {
          payment: {
            billing_details: {
              email: customer.email || selectedOrderDetails.ordered_by?.email_address
            }
          },
          order_id: orderId,
          amount: summaryData.totalCost,
          discount: 0,
          extension_type: 'rental_extension',
          intended_pickup_date: summaryData?.requestData?.intended_pickup_date // Pass intended date
        };
        
        console.log('Creating extension checkout session:', checkoutData);
        const session = await stripeExtensionCheckout(checkoutData);
        console.log('Stripe session response:', session);
        
        if (session?.data?.sessionId) {
          const stripe = await stripePromise;
          const result = await stripe.redirectToCheckout({
            sessionId: session.data.sessionId
          });
          
          if (result.error) {
            console.error('Stripe redirect error:', result.error);
            message.error('Payment redirection failed. Please try again.');
            setIsProcessingPayment(false); 
            return;
          }
        
          message.success('Redirecting to payment...');
          
          // Call onSuccess callback if provided
          if (onSuccess) {
            onSuccess();
          }
        } else {
          message.error('Failed to create payment session. Please try again.');
          setIsProcessingPayment(false); 
        }
      } else {
        message.error(updateRes?.data?.message || 'Failed to update order. Please try again.');
        setIsProcessingPayment(false); 
      }
    } catch (error) {
      console.error('Error processing extension request:', error);
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Server error occurred';
        message.error(`Failed to process extension: ${errorMessage}`);
      } else if (error.request) {
        message.error('Network error. Please check your connection and try again.');
      } else {
        message.error('Failed to process extension request. Please try again.');
      }
      setIsProcessingPayment(false); 
    }
  };

  if (!summaryData) return null;

  const getPickupDate = () => {
    // Try multiple possible locations for pickup date
    return summaryData?.requestData?.pickup_date || 
           summaryData?.pickupDate || 
           summaryData?.newPickupDate ||
           'N/A';
  };

  const displayPickupDate = getPickupDate();

  return (
    <Modal
      footer={false}
      centered
      closable={false}
      open={open}
      width={450}
      onCancel={onClose}
      maskClosable={!isProcessingPayment} 
      keyboard={!isProcessingPayment} 
      bodyStyle={{ padding: '0' }}
      title={
        <div className="d-flex justify-content-between align-items-center w-100">
          <span></span>
          {!isProcessingPayment && (
            <CloseOutlined
              className="cursor-pointer new-modal-close-icon"
              onClick={onClose}
            />
          )}
        </div>
      }
    >
      {/* Loading Overlay */}
      {isProcessingPayment && (
        <div className="loading-overlay">
          <div className="loading-content">
            <Spin size="large" />
            <div className="loading-text">
              Processing your request...
            </div>
          </div>
        </div>
      )}

      <div className="new-modal-container">
        {/* Header */}
        <div className="new-modal-header">
          Extension Summary
        </div>

        {/* Content */}
        <div className="new-modal-content">
          {/* Package Section */}
          <div className="section-container">
            <div className="new-section-title">
              Package:
            </div>
            <div className="package-item-row">
              <span className="item-name">
                {summaryData.boxPackage?.name || 'N/A'}
              </span>
              <span className="item-price">
                ${summaryData.boxPackage?.price || 0}
              </span>
            </div>
          </div>

          {/* Moving Supplies Section */}
          {summaryData.movingSupplies?.length > 0 && (
            <div className="section-container">
              <div className="new-section-title">
                Moving Supplies:
              </div>
              {summaryData.movingSupplies.map((item, index) => (
                <div
                  key={index}
                  className="supply-item-row"
                >
                  <span className="item-name">
                    {item.name || 'N/A'}
                  </span>
                  <span className="item-price">
                    ${item.price || 0}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Pickup Date Section */}
          <div className="section-container">
            <div className="new-section-title">
              New Pickup Date:
            </div>
            <div className="pickup-date-text">
              {displayPickupDate}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="new-modal-footer">
          <div className="total-cost-container">
            <span>Total Cost</span>
            <span>${summaryData.totalCost || 0}.00</span>
          </div>

          <Button
            type="primary"
            size="large"
            block
            loading={isProcessingPayment}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePayAndRequest();
            }}
            disabled={isProcessingPayment}
            className="pay-button"
          >
            {isProcessingPayment ? 'Processing...' : 'Pay & Extend'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

RentalExtensionSummaryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  summaryData: PropTypes.shape({
    extensionWeeks: PropTypes.number,
    extensionDays: PropTypes.number,       
    extensionType: PropTypes.string, 
    totalCost: PropTypes.number,
    boxPackage: PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.number,
    }),
    movingSupplies: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        price: PropTypes.number,
      })
    ),
    pickupDate: PropTypes.string,
    newPickupDate: PropTypes.string,
    requestData: PropTypes.shape({
      pickup_date: PropTypes.string,
      intended_pickup_date: PropTypes.string,
      box_package_price: PropTypes.number,
      moving_supplies_price: PropTypes.number,
      total_extension_price: PropTypes.number,
      extension_type: PropTypes.string,
      delivery_date: PropTypes.string,
      existing_rental_weeks: PropTypes.number,
      rental_period: PropTypes.string,
    }),
  }),
  isProcessing: PropTypes.bool,
  selectedExtendOrder: PropTypes.object,
  selectedOrderDetails: PropTypes.object,
  customer: PropTypes.object,
  onSuccess: PropTypes.func
};

export default RentalExtensionSummaryModal;